import React from "react";
import { Button, Card, Input, Select, Space, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Game } from "../../../../services/gameQuiz/gameQuiz.types";
import ImageUploadInput from "../ImageUploadInput";
import { generateOptionId } from "../gameTypes";
import TtsAudioControls from "../TtsAudioControls";
import OptionRichText from "../OptionRichText";

const stripHtml = (html: string): string => {
  if (!html) return "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return (tmp.textContent || tmp.innerText || "").trim();
};

type Props = {
  game: Extract<Game, { type: "categorization" }>;
  onChange: (game: Extract<Game, { type: "categorization" }>) => void;
  generateAudio?: (text: string) => Promise<string>;
};

const CategorizationEditor: React.FC<Props> = ({
  game,
  onChange,
  generateAudio,
}) => {
  // Defensive: partial/legacy games may arrive without these arrays.
  // Fall back to empty lists so the map() calls below don't crash.
  const categories: typeof game.categories = Array.isArray(game.categories)
    ? game.categories
    : [];
  const items: typeof game.items = Array.isArray(game.items) ? game.items : [];
  const distractors: NonNullable<typeof game.distractors> = Array.isArray(
    game.distractors,
  )
    ? game.distractors
    : [];

  const updateCategory = (
    id: string,
    patch: Partial<(typeof game.categories)[number]>,
  ) => {
    onChange({
      ...game,
      categories: categories.map((c) =>
        c.id === id ? { ...c, ...patch } : c,
      ),
    });
  };

  const addCategory = () => {
    onChange({
      ...game,
      categories: [
        ...categories,
        { id: generateOptionId(), name: "" },
      ],
    });
  };

  const removeCategory = (id: string) => {
    onChange({
      ...game,
      categories: categories.filter((c) => c.id !== id),
      items: items.filter((i) => i.categoryId !== id),
    });
  };

  const updateItem = (
    id: string,
    patch: Partial<(typeof game.items)[number]>,
  ) => {
    onChange({
      ...game,
      items: items.map((i) => (i.id === id ? { ...i, ...patch } : i)),
    });
  };

  const addItem = () => {
    const firstCategoryId = categories[0]?.id;
    if (!firstCategoryId) return;
    onChange({
      ...game,
      items: [
        ...items,
        {
          id: generateOptionId(),
          text: "",
          categoryId: firstCategoryId,
        },
      ],
    });
  };

  const removeItem = (id: string) => {
    onChange({ ...game, items: items.filter((i) => i.id !== id) });
  };

  const updateDistractor = (
    id: string,
    patch: Partial<NonNullable<typeof game.distractors>[number]>,
  ) => {
    onChange({
      ...game,
      distractors: distractors.map((d) =>
        d.id === id ? { ...d, ...patch } : d,
      ),
    });
  };

  const addDistractor = () => {
    onChange({
      ...game,
      distractors: [...distractors, { id: generateOptionId(), text: "" }],
    });
  };

  const removeDistractor = (id: string) => {
    onChange({
      ...game,
      distractors: distractors.filter((d) => d.id !== id),
    });
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }} size="middle">
      <div>
        <label>Pregunta</label>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
          <Input.TextArea
            value={game.question}
            onChange={(e) => onChange({ ...game, question: e.target.value })}
            rows={2}
            style={{ flex: 1 }}
          />
          <TtsAudioControls
            text={game.question}
            audioUrl={game.questionAudio}
            onAudioChange={(url) => onChange({ ...game, questionAudio: url })}
            generateAudio={generateAudio}
          />
        </div>
      </div>
      <div>
        <label>Imagen (opcional)</label>
        <ImageUploadInput
          value={game.image}
          onChange={(url) => onChange({ ...game, image: url })}
        />
      </div>
      <div>
        <label>Categorías</label>
        {categories.map((cat) => (
          <Space key={cat.id} style={{ display: "flex", marginTop: 8 }}>
            <Input
              value={cat.name}
              onChange={(e) => updateCategory(cat.id, { name: e.target.value })}
              placeholder="Nombre de la categoría"
              style={{ minWidth: 240 }}
            />
            <TtsAudioControls
              text={cat.name}
              audioUrl={cat.audio}
              onAudioChange={(url) => updateCategory(cat.id, { audio: url })}
              generateAudio={generateAudio}
            />
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => removeCategory(cat.id)}
            />
          </Space>
        ))}
        <div style={{ marginTop: 8 }}>
          <Button
            icon={<PlusOutlined />}
            type="dashed"
            block
            onClick={addCategory}
          >
            Agregar categoría
          </Button>
        </div>
      </div>
      <div>
        <label>Items</label>
        {items.map((item) => (
          <Card key={item.id} size="small" style={{ marginTop: 8 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <OptionRichText
                value={item.text}
                onChange={(html) => updateItem(item.id, { text: html })}
                placeholder="Texto del item"
              />
              <TtsAudioControls
                text={stripHtml(item.text)}
                audioUrl={item.audio}
                onAudioChange={(url) => updateItem(item.id, { audio: url })}
                generateAudio={generateAudio}
              />
              <Select
                style={{ minWidth: 180 }}
                value={item.categoryId}
                onChange={(v) => updateItem(item.id, { categoryId: v })}
                options={categories.map((c) => ({
                  label: c.name || "(sin nombre)",
                  value: c.id,
                }))}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeItem(item.id)}
              />
            </div>
            <div style={{ marginTop: 8 }}>
              <ImageUploadInput
                value={item.image}
                onChange={(url) => updateItem(item.id, { image: url })}
              />
            </div>
          </Card>
        ))}
        <div style={{ marginTop: 8 }}>
          <Button
            icon={<PlusOutlined />}
            type="dashed"
            block
            onClick={addItem}
            disabled={categories.length === 0}
          >
            Agregar item
          </Button>
        </div>
      </div>
      <div>
        <label>Distractores (no pertenecen a ninguna categoría)</label>
        <div>
          <Typography.Text type="secondary">
            Aparecen mezclados con los items; el alumno debe dejarlos sin
            colocar. Colocar un distractor cuenta como error.
          </Typography.Text>
        </div>
        {distractors.map((distractor) => (
          <Card key={distractor.id} size="small" style={{ marginTop: 8 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
              <OptionRichText
                value={distractor.text}
                onChange={(html) =>
                  updateDistractor(distractor.id, { text: html })
                }
                placeholder="Texto del distractor"
              />
              <TtsAudioControls
                text={stripHtml(distractor.text)}
                audioUrl={distractor.audio}
                onAudioChange={(url) =>
                  updateDistractor(distractor.id, { audio: url })
                }
                generateAudio={generateAudio}
              />
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={() => removeDistractor(distractor.id)}
              />
            </div>
            <div style={{ marginTop: 8 }}>
              <ImageUploadInput
                value={distractor.image}
                onChange={(url) =>
                  updateDistractor(distractor.id, { image: url })
                }
              />
            </div>
          </Card>
        ))}
        <div style={{ marginTop: 8 }}>
          <Button
            icon={<PlusOutlined />}
            type="dashed"
            block
            onClick={addDistractor}
          >
            Agregar distractor
          </Button>
        </div>
      </div>
    </Space>
  );
};

export default CategorizationEditor;
